// ============================================================
// SkyPlus Sector Switcher (tenant-mode aware)
// app/components/skyplus/shared/sector/sector-switcher.component.ts
//
// Renders differently based on tenant mode:
//
//   mode=client -> invisible, does not render at all.
//                  Client users don't see anything about packs.
//
//   mode=dev    -> static pill showing the active pack for
//                  debugging. Not clickable.
//
//   mode=demo   -> interactive dropdown allowing switch between
//                  loaded packs. The "chameleon demo" affordance.
//
// The component itself is always in the template. The tenant mode
// check is inside the component, so the parent doesn't need to know.
// ============================================================

import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SectorService } from './sector.service';
import { Pack, PackSummary, TenantMode } from './sector.model';

@Component({
  selector:    'sp-sector-switcher',
  templateUrl: './sector-switcher.component.html',
  styleUrls:   ['./sector-switcher.component.scss']
})
export class SectorSwitcherComponent implements OnInit, OnDestroy {

  mode:            TenantMode          = 'client';
  activePack:      Pack         | null = null;
  availablePacks:  PackSummary[]       = [];
  dropdownOpen:    boolean             = false;

  private subs: Subscription[] = [];

  constructor(private sector: SectorService) {}

  ngOnInit(): void {
    this.subs.push(this.sector.tenantMode$.subscribe(m => this.mode = m));
    this.subs.push(this.sector.pack$.subscribe(p => this.activePack = p));
    this.subs.push(this.sector.availablePacks$.subscribe(list => this.availablePacks = list));
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  get shouldRender(): boolean {
    return this.mode !== 'client';
  }

  get isInteractive(): boolean {
    return this.mode === 'demo';
  }

  get badgeText(): string {
    const name = this.activePack?.packName || 'GN';
    return name.substring(0, 2).toUpperCase();
  }

  toggleDropdown(): void {
    if (!this.isInteractive) return;
    this.dropdownOpen = !this.dropdownOpen;
  }

  switchTo(pack: PackSummary): void {
    if (!this.isInteractive || pack.packId === this.activePack?.packId) {
      this.dropdownOpen = false;
      return;
    }
    console.log('[SectorSwitcher] Switch requested:', pack.packId);
    this.dropdownOpen = false;
  }
}
